/**
 * Einsatzplan (Read-Only Shift View for Employees)
 * 
 * Shows shifts at the employee's assigned locations in a weekly calendar view.
 * This is a read-only view - employees cannot modify shifts.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { EmployeeHoverCard } from '../components/EmployeeHoverCard';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, parseISO, isSameDay, startOfMonth, endOfMonth, addMonths, subMonths, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  Users,
  Calendar,
  CalendarPlus,
} from 'lucide-react';
import { CalendarSubscribeDialog } from '../components/CalendarSubscribeDialog';

const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export default function Einsatzplan() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [myLocationIds, setMyLocationIds] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [currentWeek, setCurrentWeek] = useState(() => {
    // Optionaler URL-Param ?week=YYYY-MM-DD steuert Wochenstart
    const wParam = new URLSearchParams(window.location.search).get('week');
    if (wParam) {
      const parsed = new Date(wParam);
      if (!Number.isNaN(parsed.getTime())) {
        return startOfWeek(parsed, { weekStartsOn: 1 });
      }
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  });
  const [massnahmen, setMassnahmen] = useState([]);
  // Persistierter Filter: Mitarbeiter sehen standardmäßig nur eigene Einsätze
  const [showOnlyMine, setShowOnlyMine] = useState(() => {
    try {
      const stored = localStorage.getItem('teamhub.einsatzplan.show_only_mine');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('teamhub.einsatzplan.show_only_mine', String(showOnlyMine));
    } catch {
      /* localStorage nicht verfügbar — egal */
    }
  }, [showOnlyMine]);

  const [icalDialogOpen, setIcalDialogOpen] = useState(false);

  // Ansicht: 'week' oder 'month' (persistiert)
  const [viewMode, setViewMode] = useState(() => {
    // URL-Param ?week=... erzwingt Wochenansicht
    const hasWeekParam = !!new URLSearchParams(window.location.search).get('week');
    if (hasWeekParam) return 'week';
    try {
      const stored = localStorage.getItem('teamhub.einsatzplan.view_mode');
      return stored === 'month' ? 'month' : 'week';
    } catch {
      return 'week';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('teamhub.einsatzplan.view_mode', viewMode);
    } catch {
      /* egal */
    }
  }, [viewMode]);

  // Calculate range (week or month) — `currentWeek` ist Anker, nicht zwingend Wochenstart
  const { weekStart, weekEnd, weekDays, rangeLabel } = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentWeek);
      const monthEnd = endOfMonth(currentWeek);
      const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return {
        weekStart: gridStart,
        weekEnd: gridEnd,
        weekDays: eachDayOfInterval({ start: gridStart, end: gridEnd }),
        rangeLabel: format(currentWeek, 'MMMM yyyy', { locale: de }),
      };
    }
    const start = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const end = endOfWeek(currentWeek, { weekStartsOn: 1 });
    return {
      weekStart: start,
      weekEnd: end,
      weekDays: eachDayOfInterval({ start, end }),
      rangeLabel: `${format(start, 'dd. MMMM', { locale: de })} - ${format(end, 'dd. MMMM yyyy', { locale: de })}`,
    };
  }, [currentWeek, viewMode]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadShifts = useCallback(async () => {
    if (!selectedLocation) return;
    setLoading(true);
    try {
      const params = {
        start_date: format(weekStart, 'yyyy-MM-dd'),
        end_date: format(weekEnd, 'yyyy-MM-dd'),
        location_id: selectedLocation,
      };
      const response = await api.get('/shifts', { params });
      setShifts(response.data);
    } catch (error) {
      console.error('Error loading shifts:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd, selectedLocation]);

  // Load shifts when week or location changes
  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  // Load holidays for the selected location and current range
  useEffect(() => {
    if (!selectedLocation) {
      setHolidaysByDate({});
      return;
    }
    let cancelled = false;
    api.get('/holidays-by-location', {
      params: {
        location_id: selectedLocation,
        start_date: format(weekStart, 'yyyy-MM-dd'),
        end_date: format(weekEnd, 'yyyy-MM-dd'),
      },
    })
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data?.holidays || []).forEach((h) => { map[h.date] = h.name; });
        setHolidaysByDate(map);
      })
      .catch(() => {
        if (!cancelled) setHolidaysByDate({});
      });
    return () => { cancelled = true; };
  }, [selectedLocation, weekStart, weekEnd]);

  const loadInitialData = async () => {
    try {
      const [locationsRes, massnahmenRes, profileRes] = await Promise.all([
        api.get('/locations'),
        api.get('/massnahmen'),
        api.get('/employees/me').catch(() => ({ data: null })),
      ]);
      
      setLocations(locationsRes.data);
      setMassnahmen(massnahmenRes.data);
      
      if (profileRes.data) {
        setMyEmployee(profileRes.data);
        const locationIds = [
          profileRes.data.location_id,
          ...(profileRes.data.additional_location_ids || [])
        ].filter(Boolean);
        setMyLocationIds(locationIds);
        
        // Auto-select first location
        if (locationIds.length > 0) {
          setSelectedLocation(locationIds[0]);
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // Filter locations to only show employee's locations
  const myLocations = useMemo(() => {
    return locations.filter(l => myLocationIds.includes(l.id));
  }, [locations, myLocationIds]);

  // Filter shifts by "Nur meine Einsätze" toggle
  const myEmployeeId = myEmployee?.id || user?.employee_id;
  const visibleShifts = useMemo(() => {
    if (!showOnlyMine || !myEmployeeId) return shifts;
    return shifts.filter((s) => s.employee_id === myEmployeeId);
  }, [shifts, showOnlyMine, myEmployeeId]);

  // Group shifts by date and then by Maßnahme
  const shiftsByDay = useMemo(() => {
    const grouped = {};
    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      grouped[dateStr] = {};
    });

    visibleShifts.forEach(shift => {
      if (grouped[shift.date]) {
        const massnahmeId = shift.massnahme_id || 'other';
        if (!grouped[shift.date][massnahmeId]) {
          grouped[shift.date][massnahmeId] = [];
        }
        grouped[shift.date][massnahmeId].push(shift);
      }
    });

    return grouped;
  }, [visibleShifts, weekDays]);

  // Get Maßnahme info
  const getMassnahme = (id) => massnahmen.find(m => m.id === id);

  // Navigation
  const goToPreviousWeek = () =>
    setCurrentWeek(viewMode === 'month' ? subMonths(currentWeek, 1) : subWeeks(currentWeek, 1));
  const goToNextWeek = () =>
    setCurrentWeek(viewMode === 'month' ? addMonths(currentWeek, 1) : addWeeks(currentWeek, 1));
  const goToToday = () =>
    setCurrentWeek(viewMode === 'month' ? new Date() : startOfWeek(new Date(), { weekStartsOn: 1 }));

  return (
    <DashboardLayout wide>
      <div className="p-6 space-y-6" data-testid="einsatzplan-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="h-6 w-6" />
              {showOnlyMine && myEmployeeId ? 'Meine Einsätze' : 'Einsatzplan'}
            </h1>
            <p className="text-muted-foreground">
              {showOnlyMine && myEmployeeId
                ? 'Wochenübersicht Ihrer eigenen Einsätze'
                : 'Übersicht der Einsätze an Ihren zugewiesenen Standorten'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Ansicht: Woche / Monat */}
            <div
              className="inline-flex rounded-md border bg-card p-0.5"
              data-testid="einsatzplan-view-mode-switch"
              role="tablist"
            >
              <button
                type="button"
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
                  viewMode === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="einsatzplan-view-week"
              >
                Woche
              </button>
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
                  viewMode === 'month' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="einsatzplan-view-month"
              >
                Monat
              </button>
            </div>

            {/* Kalender abonnieren */}
            {myEmployeeId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIcalDialogOpen(true)}
                data-testid="einsatzplan-subscribe-button"
                className="whitespace-nowrap"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Kalender abonnieren
              </Button>
            )}

            {/* Filter-Toggle: Nur meine Einsätze */}
            {myEmployeeId && (
              <div
                className="flex items-center gap-2 rounded-md border bg-card px-3 py-2"
                data-testid="einsatzplan-filter-mine"
              >
                <Switch
                  id="einsatzplan-only-mine"
                  checked={showOnlyMine}
                  onCheckedChange={setShowOnlyMine}
                  data-testid="einsatzplan-toggle-only-mine"
                />
                <Label
                  htmlFor="einsatzplan-only-mine"
                  className="text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  Nur meine Einsätze
                </Label>
              </div>
            )}

            {/* Location Filter - only show employee's locations */}
            {myLocations.length > 1 && (
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="einsatzplan-location-select">
                  <MapPin className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Standort wählen" />
                </SelectTrigger>
                <SelectContent>
                  {myLocations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* No Location Assigned Warning */}
        {myLocationIds.length === 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center">
              <MapPin className="h-10 w-10 mx-auto text-amber-500 mb-3" />
              <h3 className="font-medium text-lg">Kein Standort zugewiesen</h3>
              <p className="text-muted-foreground mt-1">
                Sie sind keinem Standort zugewiesen. Bitte wenden Sie sich an Ihren Administrator.
              </p>
            </CardContent>
          </Card>
        )}

        {myLocationIds.length > 0 && (
          <>
            {/* Week Navigation */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={goToToday}>Heute</Button>
                    <div className="text-center">
                      <h2 className="text-lg font-semibold">
                        {rangeLabel}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {viewMode === 'week' ? `KW ${format(weekStart, 'w', { locale: de })} • ` : ''}
                        {myLocations.find(l => l.id === selectedLocation)?.name}
                      </p>
                    </div>
                  </div>
                  
                  <Button variant="outline" size="icon" onClick={goToNextWeek}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

        {/* Week / Month Calendar */}
        {loading ? (
          <div className={viewMode === 'month' ? 'grid grid-cols-7 gap-1' : 'grid grid-cols-7 gap-2'}>
            {[...Array(viewMode === 'month' ? 35 : 7)].map((_, i) => (
              <Skeleton key={`skel-${i}`} className={viewMode === 'month' ? 'h-24' : 'h-64'} />
            ))}
          </div>
        ) : viewMode === 'month' ? (
          <div className="space-y-2" data-testid="einsatzplan-month-grid">
            {/* Wochentag-Header */}
            <div className="grid grid-cols-7 gap-1 text-xs font-medium text-muted-foreground">
              {WEEKDAY_NAMES.map((wd) => (
                <div key={wd} className="text-center py-1">{wd}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {weekDays.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayShifts = shiftsByDay[dateStr] || {};
                const isToday = isSameDay(day, new Date());
                const inMonth = isSameMonth(day, currentWeek);
                const flatShifts = Object.values(dayShifts).flat();
                const holidayName = holidaysByDate[dateStr];
                return (
                  <Card
                    key={dateStr}
                    className={`min-h-[88px] sm:min-h-[110px] p-1.5 ${isToday ? 'ring-2 ring-primary' : ''} ${holidayName ? 'bg-purple-50 dark:bg-purple-900/20' : ''} ${inMonth ? '' : 'opacity-40 bg-muted/40'}`}
                    data-testid={`day-${dateStr}`}
                    title={holidayName ? `Feiertag: ${holidayName}` : undefined}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>
                        {format(day, 'd')}
                      </span>
                      {flatShifts.length > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                          {flatShifts.length}
                        </Badge>
                      )}
                    </div>
                    {holidayName && (
                      <div
                        className="text-[10px] truncate text-purple-700 dark:text-purple-300 font-medium mb-1"
                        data-testid={`holiday-${dateStr}`}
                      >
                        🎉 {holidayName}
                      </div>
                    )}
                    <div className="space-y-0.5">
                      {flatShifts.slice(0, 3).map((shift) => {
                        const isMe = shift.employee_id === myEmployeeId;
                        return (
                          <div
                            key={shift.id}
                            className={`text-[10px] truncate rounded px-1 py-0.5 ${isMe ? 'font-medium' : ''}`}
                            style={{
                              backgroundColor: shift.massnahme_color ? `${shift.massnahme_color}30` : '#f3f4f6',
                              borderLeft: `2px solid ${shift.massnahme_color || '#9ca3af'}`,
                            }}
                            title={`${shift.start_time && shift.end_time ? `${shift.start_time}-${shift.end_time}` : 'Ganztags'} ${shift.massnahme_name || ''}`}
                          >
                            {shift.start_time?.slice(0, 5) || 'Ganztags'} {shift.massnahme_short_code || shift.massnahme_name || ''}
                          </div>
                        );
                      })}
                      {flatShifts.length > 3 && (
                        <div className="text-[10px] text-muted-foreground px-1">
                          + {flatShifts.length - 3} weitere
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
            {weekDays.map((day, index) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const dayShifts = shiftsByDay[dateStr] || {};
              const isToday = isSameDay(day, new Date());
              const totalShifts = Object.values(dayShifts).flat().length;
              const holidayName = holidaysByDate[dateStr];

              return (
                <Card 
                  key={dateStr} 
                  className={`min-h-[200px] ${isToday ? 'ring-2 ring-primary' : ''} ${holidayName ? 'border-purple-300 dark:border-purple-700' : ''}`}
                  data-testid={`day-${dateStr}`}
                >
                  <CardHeader className={`p-3 pb-2 ${isToday ? 'bg-primary/10' : holidayName ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-muted/30'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{WEEKDAY_NAMES[index]}</span>
                      <span className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                        {format(day, 'd')}
                      </span>
                    </div>
                    {holidayName && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] w-fit bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-100 border-0"
                        data-testid={`holiday-${dateStr}`}
                      >
                        🎉 {holidayName}
                      </Badge>
                    )}
                    {totalShifts > 0 && (
                      <Badge variant="secondary" className="text-xs w-fit">
                        {totalShifts} Einsätze
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="p-2 space-y-2">
                    {Object.entries(dayShifts).length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        {showOnlyMine && myEmployeeId ? 'Kein eigener Einsatz' : 'Keine Einsätze'}
                      </p>
                    ) : (
                      Object.entries(dayShifts).map(([massnahmeId, massnahmeShifts]) => {
                        const massnahme = getMassnahme(massnahmeId);
                        return (
                          <div 
                            key={massnahmeId} 
                            className="rounded-lg p-2 text-xs"
                            style={{ 
                              backgroundColor: massnahme?.color ? `${massnahme.color}20` : '#f3f4f6',
                              borderLeft: `3px solid ${massnahme?.color || '#9ca3af'}`
                            }}
                          >
                            <div className="font-medium mb-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {massnahme?.name || 'Unbekannt'}
                              {massnahme?.start_time && massnahme?.end_time && (
                                <span className="text-muted-foreground ml-auto">
                                  {massnahme.start_time} - {massnahme.end_time}
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              {massnahmeShifts.map(shift => {
                                const isMe = shift.employee_id === user?.employee_id;
                                return (
                                  <div 
                                    key={shift.id} 
                                    className={`flex items-center gap-1 p-1 rounded ${isMe ? 'bg-primary/20 font-medium' : 'bg-white/50'}`}
                                  >
                                    <User className="h-3 w-3" />
                                    <span className="truncate">
                                      <EmployeeHoverCard employeeId={shift.employee_id}>
                                        <span className="cursor-pointer hover:text-primary hover:underline" data-testid={`einsatzplan-name-${shift.employee_id}`}>
                                          {shift.employee_name}
                                        </span>
                                      </EmployeeHoverCard>
                                      {isMe && <span className="text-primary ml-1">(Sie)</span>}
                                    </span>
                                    {shift.is_additional_location && (
                                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-auto">
                                        Zusatz
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Legende</CardTitle>
          </CardHeader>
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-4 text-sm">
              {massnahmen.slice(0, 6).map(m => (
                <div key={m.id} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: m.color || '#9ca3af' }}
                  />
                  <span>{m.name}</span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary/20 border-2 border-primary" />
                <span>Ihre Einsätze</span>
              </div>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
      <CalendarSubscribeDialog open={icalDialogOpen} onOpenChange={setIcalDialogOpen} />
    </DashboardLayout>
  );
}
